import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Transition } from "@headlessui/react";
import { usePopperTooltip, type PopperOptions } from "react-popper-tooltip";
import { FaCaretDown, FaCaretUp } from "react-icons/fa6";
import { SingleSelectDropdownProps } from "./types";

const floatingMenuOptions: PopperOptions = {
  strategy: "fixed",
  modifiers: [
    { name: "flip", enabled: false },
    { name: "offset", options: { offset: [0, 8] } },
    {
      name: "matchTriggerWidth",
      enabled: true,
      phase: "beforeWrite",
      requires: ["computeStyles"],
      fn: ({ state }) => {
        state.styles.popper.width = `${state.rects.reference.width}px`;
        const availableHeight = Math.max(
          0,
          window.innerHeight -
            state.elements.reference.getBoundingClientRect().bottom -
            16
        );
        Object.assign(state.styles.popper, {
          "--dropdown-available-height": `${availableHeight}px`,
        });
      },
    },
  ],
};

const Dropdown = ({
  buttonAriaDescribedBy,
  buttonAriaLabel,
  buttonAriaLabelledBy,
  buttonId,
  label,
  children,
  isOpen,
  setIsOpen,
  className,
  buttonStyle,
  buttonDisabled = false,
  showCaret = true,
  menuClassName,
  closeOnScroll = false,
  portal = false,
}: SingleSelectDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const { getTooltipProps, setTooltipRef, setTriggerRef } = usePopperTooltip(
    {
      visible: portal && isOpen,
      trigger: null,
      closeOnOutsideClick: false,
      placement: "bottom-start",
    },
    floatingMenuOptions
  );
  const setButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      buttonRef.current = node;
      if (portal) setTriggerRef(node);
    },
    [portal, setTriggerRef]
  );
  const setFloatingMenuRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node && menuRef.current?.contains(document.activeElement)) {
        buttonRef.current?.focus({ preventScroll: true });
      }
      menuRef.current = node;
      setTooltipRef(node);
    },
    [setTooltipRef]
  );

  useEffect(() => {
    if (!isOpen) return;
    const dismiss = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !dropdownRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    if (!isOpen && menuRef.current?.contains(document.activeElement)) {
      buttonRef.current?.focus({ preventScroll: true });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!closeOnScroll || !isOpen) return;
    const handleScroll = (event: Event) => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        (dropdownRef.current.contains(event.target) ||
          menuRef.current?.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [closeOnScroll, isOpen, setIsOpen]);

  const menu = (
    <Transition
      show={isOpen}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <div
        ref={portal ? undefined : menuRef}
        className={`${
          portal ? "relative !mt-0" : "absolute left-0 z-10 mt-2"
        } origin-top-left divide-y divide-gray-100 rounded-md border border-gray-200 bg-white shadow-lg outline-none ${
          menuClassName || ""
        }`}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setIsOpen(false);
          }
        }}
      >
        {children}
      </div>
    </Transition>
  );

  return (
    <div
      className={`relative inline-block text-left ${
        isOpen && !portal ? "z-20" : ""
      } ${className}`}
      ref={dropdownRef}
    >
      <button
        ref={setButtonRef}
        id={buttonId}
        type="button"
        aria-describedby={buttonAriaDescribedBy}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={buttonAriaLabel}
        aria-labelledby={buttonAriaLabelledBy}
        onClick={() => {
          if (!buttonDisabled) {
            setIsOpen((prev) => !prev);
          }
        }}
        disabled={buttonDisabled}
        className={`inline-flex w-full items-center justify-between whitespace-nowrap rounded-md border border-gray-300 px-3 py-2 text-sm font-medium leading-5 text-gray-700 focus:outline-none ${buttonStyle}`}
      >
        {label}
        {showCaret &&
          (isOpen ? (
            <FaCaretUp className="ml-2" size={17.5} />
          ) : (
            <FaCaretDown className="ml-2" size={17.5} />
          ))}
      </button>

      {portal
        ? isOpen &&
          createPortal(
            <div
              ref={setFloatingMenuRef}
              {...getTooltipProps({
                className: "z-50 max-w-[calc(100vw-1rem)]",
              })}
            >
              {menu}
            </div>,
            document.body
          )
        : menu}
    </div>
  );
};

export default Dropdown;
